import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  MdAdd,
  MdEdit,
  MdMyLocation,
  MdVisibility,
} from 'react-icons/md';
import {
  AppCard,
  AppInput,
  AppSelect,
  GooglePlacesSearch,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  TablePagination,
  PageLoader,
} from '../../../../components/ui';
import VillageMap from '../../../../components/map/VillageMap';
import { ActiveBadge } from '../MasterDataPage';
import {
  VILLAGE_CATEGORIES,
  lookupOfficialVillageGeo,
  normalizeVillageName,
  villageMasterSeed,
} from '../../../../data/master/villages';
import {
  createVillage,
  listVillages,
  seedMundraVillages,
  updateVillage,
} from '../../../../repositories/village.repository';
import { searchVillageOnMap } from '../../../../utils/geocode';
import { lookupPinForVillage, fetchPinFromIndiaPost } from '../../../../utils/pinLookup';
import { usePagination } from '../../../../hooks/usePagination';
import { useTableSort } from '../../../../hooks/useTableSort';

const emptyForm = {
  villageName: '',
  talukaName: 'Mundra',
  district: 'Kachchh',
  state: 'Gujarat',
  country: 'India',
  gramPanchayat: '',
  pin: '',
  category: 'Rural',
  googlePlaceId: null,
  latitude: null,
  longitude: null,
  boundary: null,
  active: true,
};

function findSeedVillage(name, talukaName) {
  const n = normalizeVillageName(name);
  if (!n) return null;
  const t = normalizeVillageName(talukaName);
  return (
    villageMasterSeed.find(
      (v) =>
        normalizeVillageName(v.villageName) === n &&
        (!t || normalizeVillageName(v.talukaName) === t),
    ) ||
    villageMasterSeed.find((v) => normalizeVillageName(v.villageName) === n) ||
    null
  );
}

export default function VillagesPage({ embedded = false } = {}) {
  const toast = useToast();
  const addModal = useDisclosure();
  const detailModal = useDisclosure();
  const [villages, setVillages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [talukaFilter, setTalukaFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [mapSearching, setMapSearching] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [selected, setSelected] = useState(null);

  const loadVillages = useCallback(
    async ({ showSpinner = true } = {}) => {
      if (showSpinner) setLoading(true);
      try {
        const result = await listVillages({ pageSize: 500 });
        setVillages(result.items || []);
      } catch (err) {
        toast({
          title: err?.message || 'Failed to load villages',
          status: 'error',
          position: 'top-right',
        });
        setVillages([]);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    loadVillages();
  }, [loadVillages]);

  const talukas = useMemo(
    () => [...new Set(villages.map((v) => v.talukaName).filter(Boolean))].sort(),
    [villages],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return villages.filter((v) => {
      if (talukaFilter && v.talukaName !== talukaFilter) return false;
      if (categoryFilter && v.category !== categoryFilter) return false;
      if (!q) return true;
      return (
        (v.villageName || '').toLowerCase().includes(q) ||
        (v.pin || '').includes(q) ||
        (v.gramPanchayat || '').toLowerCase().includes(q) ||
        (v.talukaName || '').toLowerCase().includes(q)
      );
    });
  }, [villages, search, talukaFilter, categoryFilter]);

  const { sorted, sort, toggleSort } = useTableSort(filtered);

  const {
    pageItems,
    page,
    pageSize,
    totalCount,
    totalPages,
    setPage,
    setPageSize,
  } = usePagination(sorted);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    addModal.onOpen();
  };

  const openEdit = (village) => {
    setEditingId(village.id);
    setForm({
      villageName: village.villageName || '',
      talukaName: village.talukaName || 'Mundra',
      district: village.district || 'Kachchh',
      state: village.state || 'Gujarat',
      country: village.country || 'India',
      gramPanchayat: village.gramPanchayat || '',
      pin: village.pin || '',
      category: village.category || 'Rural',
      googlePlaceId: village.googlePlaceId || null,
      latitude: village.latitude ?? null,
      longitude: village.longitude ?? null,
      boundary: village.boundary ?? null,
      active: village.active !== false,
    });
    addModal.onOpen();
  };

  const openDetail = (village) => {
    setSelected(village);
    detailModal.onOpen();
  };

  const handleLocateOnMap = async () => {
    if (!form.villageName.trim()) {
      toast({
        title: 'Enter village name first',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    setMapSearching(true);
    try {
      const result = await searchVillageOnMap({
        villageName: form.villageName.trim(),
        talukaName: form.talukaName,
        district: form.district,
        state: form.state,
        country: form.country,
      });
      if (!result) {
        toast({
          title: 'No map match',
          description: 'Try a clearer name, or click on the map to pin the location.',
          status: 'warning',
          position: 'top-right',
        });
        return;
      }
      setForm((f) => {
        const geo =
          lookupOfficialVillageGeo(form.villageName.trim(), form.talukaName) ||
          lookupOfficialVillageGeo(form.villageName.trim());
        return {
          ...f,
          latitude: result.latitude,
          longitude: result.longitude,
          boundary: result.boundary,
          ...(result.pin ? { pin: result.pin } : {}),
          ...(result.source === 'official' && geo?.taluka
            ? { talukaName: geo.taluka }
            : {}),
        };
      });
      toast({
        title: result.source === 'official' ? 'Official border loaded' : 'Location found',
        description: [
          result.displayName,
          result.pin ? `PIN ${result.pin}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
        status: 'success',
        position: 'top-right',
      });
    } catch {
      toast({
        title: 'Map search failed',
        description: 'You can still click on the map to set the pin.',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setMapSearching(false);
    }
  };

  const applyVillageAutofill = (patch) => {
    setForm((f) => ({ ...f, ...patch }));
  };

  const handlePlaceResolved = (place) => {
    const villageName =
      place.village ||
      place.locality ||
      place.mainText ||
      form.villageName ||
      '';
    const talukaName = place.taluka || form.talukaName || 'Mundra';
    const seed = findSeedVillage(villageName, talukaName);
    const geo =
      lookupOfficialVillageGeo(villageName, talukaName) ||
      lookupOfficialVillageGeo(villageName) ||
      null;
    const pin =
      seed?.pin ||
      place.pin ||
      lookupPinForVillage(villageName, {
        latitude: geo?.latitude ?? place.latitude,
        longitude: geo?.longitude ?? place.longitude,
      }) ||
      '';

    applyVillageAutofill({
      villageName: seed?.villageName || villageName,
      talukaName: seed?.talukaName || geo?.taluka || talukaName,
      district: place.district || seed?.district || form.district || 'Kachchh',
      state: place.state || seed?.state || form.state || 'Gujarat',
      country: place.country || seed?.country || form.country || 'India',
      gramPanchayat:
        seed?.gramPanchayat || form.gramPanchayat || villageName,
      pin,
      category: seed?.category || form.category || 'Rural',
      googlePlaceId: place.googlePlaceId || place.placeId || null,
      latitude: geo?.latitude ?? place.latitude ?? null,
      longitude: geo?.longitude ?? place.longitude ?? null,
      boundary: geo?.boundary ?? seed?.boundary ?? null,
    });

    toast({
      title: 'Location autofilled',
      description: [
        seed?.villageName || villageName,
        pin ? `PIN ${pin}` : null,
        geo ? 'Official border loaded' : null,
      ]
        .filter(Boolean)
        .join(' · '),
      status: 'success',
      position: 'top-right',
      duration: 2500,
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.villageName.trim() || !form.pin.trim()) {
      toast({
        title: 'Village name and PIN are required',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    if (form.latitude == null || form.longitude == null) {
      toast({
        title: 'Set location on map',
        description: 'Search on map or click the map to drop a pin.',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    const payload = {
      villageName: form.villageName.trim(),
      talukaName: form.talukaName.trim() || 'Mundra',
      district: form.district.trim() || 'Kachchh',
      state: form.state.trim() || 'Gujarat',
      country: form.country.trim() || 'India',
      gramPanchayat: form.gramPanchayat.trim() || form.villageName.trim(),
      pin: form.pin.trim(),
      category: form.category,
      googlePlaceId: form.googlePlaceId || null,
      latitude: form.latitude,
      longitude: form.longitude,
      boundary: form.boundary,
      active: form.active,
    };

    setSaving(true);
    try {
      if (editingId != null) {
        await updateVillage(editingId, payload);
        toast({
          title: 'Village updated',
          description: payload.villageName,
          status: 'success',
          position: 'top-right',
        });
      } else {
        const created = await createVillage(payload);
        toast({
          title: 'Village saved',
          description: created?.villageName || payload.villageName,
          status: 'success',
          position: 'top-right',
        });
      }
      addModal.onClose();
      await loadVillages({ showSpinner: false });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to save village',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSeedMundra = async () => {
    setSeeding(true);
    try {
      const result = await seedMundraVillages();
      const failCount = result.failed?.length || 0;
      toast({
        title: 'Mundra seed finished',
        description: `Created ${result.created}, skipped ${result.skipped}${
          failCount ? `, failed ${failCount}` : ''
        } (of ${result.total}).`,
        status: failCount ? 'warning' : 'success',
        position: 'top-right',
        duration: 6000,
      });
      await loadVillages({ showSpinner: false });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to seed Mundra villages',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSeeding(false);
    }
  };

  if (loading) {
    return <PageLoader label="Loading villages…" />;
  }

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        {!embedded ? (
          <Box>
            <Heading size="lg">Villages</Heading>
            <Text color="text.secondary" mt={2} maxW="720px">
              Location is set from the map (search by village name or click to pin) — no manual lat/long.
            </Text>
          </Box>
        ) : (
          <Box />
        )}
        <HStack spacing={2} flexWrap="wrap">
          <OutlineButton
            size="sm"
            onClick={handleSeedMundra}
            isLoading={seeding}
            loadingText="Seeding…"
          >
            Seed Mundra villages
          </OutlineButton>
          <PrimaryButton size="sm" leftIcon={<MdAdd />} onClick={openAdd}>
            Add village
          </PrimaryButton>
        </HStack>
      </Flex>

      <AppCard mb={4}>
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3} alignItems="end">
          <Box>
            <FormLabel fontSize="13px" mb={2}>
              Search
            </FormLabel>
            <Input
              h="40px"
              fontSize="14px"
              placeholder="Name / PIN / Panchayat"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <AppSelect
            label="Taluka"
            labelFontSize="13px"
            h="40px"
            placeholder="All talukas"
            value={talukaFilter}
            onChange={(v) => setTalukaFilter(v || '')}
            options={[
              { value: '', label: 'All talukas' },
              ...talukas.map((t) => ({ value: t, label: t })),
            ]}
          />
          <AppSelect
            label="Category"
            labelFontSize="13px"
            h="40px"
            placeholder="All categories"
            value={categoryFilter}
            onChange={(v) => setCategoryFilter(v || '')}
            options={[
              { value: '', label: 'All categories' },
              ...VILLAGE_CATEGORIES.map((c) => ({ value: c, label: c })),
            ]}
          />
          <OutlineButton
            size="sm"
            w="100%"
            h="40px"
            minH="40px"
            onClick={() => {
              setSearch('');
              setTalukaFilter('');
              setCategoryFilter('');
            }}
          >
            Clear filters
          </OutlineButton>
        </SimpleGrid>
      </AppCard>

      <AppCard p={0} overflow="hidden">
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="bg.input">
              <Tr>
                <SortableTh columnKey="villageName" sort={sort} onSort={toggleSort}>
                  Village
                </SortableTh>
                <SortableTh columnKey="taluka" sort={sort} onSort={toggleSort}>
                  Taluka
                </SortableTh>
                <SortableTh columnKey="gramPanchayat" sort={sort} onSort={toggleSort}>
                  Gram Panchayat
                </SortableTh>
                <SortableTh columnKey="pin" sort={sort} onSort={toggleSort}>
                  PIN
                </SortableTh>
                <SortableTh columnKey="category" sort={sort} onSort={toggleSort}>
                  Category
                </SortableTh>
                <SortableTh columnKey="active" sort={sort} onSort={toggleSort}>
                  Status
                </SortableTh>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {pageItems.map((v) => (
                <Tr key={v.id ?? v.villageId}>
                  <Td fontWeight="600">{v.villageName}</Td>
                  <Td>{v.talukaName}</Td>
                  <Td>{v.gramPanchayat}</Td>
                  <Td>{v.pin}</Td>
                  <Td>{v.category}</Td>
                  <Td>
                    <ActiveBadge active={v.active} />
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <IconButton
                        aria-label={`Edit ${v.villageName}`}
                        icon={<MdEdit />}
                        size="sm"
                        variant="ghostInk"
                        onClick={() => openEdit(v)}
                      />
                      <IconButton
                        aria-label={`View ${v.villageName}`}
                        icon={<MdVisibility />}
                        size="sm"
                        variant="ghostInk"
                        onClick={() => openDetail(v)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
              {pageItems.length === 0 ? (
                <Tr>
                  <Td colSpan={8}>
                    <Text color="text.secondary" py={6} textAlign="center">
                      No villages match your filters.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>

        <TablePagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          px={4}
          suffix={`(total ${villages.length})`}
        />
      </AppCard>

      {/* Add / Edit village */}
      <Modal
        isOpen={addModal.isOpen}
        onClose={() => {
          if (!saving) addModal.onClose();
        }}
        size="3xl"
        isCentered
        scrollBehavior="inside"
        blockScrollOnMount
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          as="form"
          onSubmit={handleSave}
          bg="bg.surface"
          borderRadius="md"
          mx={4}
          my={4}
          maxH="90vh"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader flexShrink={0}>
            {editingId != null ? `Edit village · ${form.villageName || editingId}` : 'Add village'}
          </ModalHeader>
          <ModalCloseButton isDisabled={saving} />
          <ModalBody flex="1" overflowY="auto" minH={0} pb={4}>
            <VStack align="stretch" spacing={4}>
              <GooglePlacesSearch
                label="Search village / location"
                villages={villageMasterSeed}
                helperText="Select a place to autofill name, address, PIN, and map. You can still edit fields below."
                onResolved={handlePlaceResolved}
              />

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <AppInput
                  label="Village Name"
                  isRequired
                  value={form.villageName}
                  onChange={(e) => {
                    const villageName = e.target.value;
                    const geo =
                      lookupOfficialVillageGeo(villageName, form.talukaName) ||
                      lookupOfficialVillageGeo(villageName);
                    const seed = findSeedVillage(villageName, form.talukaName);
                    const pin =
                      seed?.pin ||
                      lookupPinForVillage(villageName, {
                        latitude: geo?.latitude,
                        longitude: geo?.longitude,
                      });
                    setForm((f) => ({
                      ...f,
                      villageName,
                      ...(seed
                        ? {
                            gramPanchayat: seed.gramPanchayat || f.gramPanchayat,
                            category: seed.category || f.category,
                            district: seed.district || f.district,
                            state: seed.state || f.state,
                            country: seed.country || f.country,
                          }
                        : {}),
                      ...(geo
                        ? {
                            latitude: geo.latitude,
                            longitude: geo.longitude,
                            boundary: geo.boundary,
                            talukaName: geo.taluka || f.talukaName,
                          }
                        : {}),
                      ...(pin ? { pin } : {}),
                    }));
                  }}
                  onBlur={async () => {
                    if (!form.villageName.trim()) return;
                    if (form.pin?.trim()) return;
                    try {
                      const pin = await fetchPinFromIndiaPost(form.villageName.trim());
                      if (pin) setForm((f) => (f.pin?.trim() ? f : { ...f, pin }));
                    } catch {
                      /* ignore */
                    }
                  }}
                  placeholder="e.g. Moti Bhujpar"
                />
                <AppInput
                  label="Taluka Name"
                  value={form.talukaName}
                  onChange={(e) => {
                    const talukaName = e.target.value;
                    const geo = lookupOfficialVillageGeo(form.villageName, talukaName);
                    setForm((f) => ({
                      ...f,
                      talukaName,
                      ...(geo
                        ? {
                            latitude: geo.latitude,
                            longitude: geo.longitude,
                            boundary: geo.boundary,
                          }
                        : {}),
                    }));
                  }}
                />
                <AppInput
                  label="District"
                  value={form.district}
                  onChange={(e) => setForm((f) => ({ ...f, district: e.target.value }))}
                />
                <AppInput
                  label="State"
                  value={form.state}
                  onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
                />
                <AppInput
                  label="Country"
                  value={form.country}
                  onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                />
                <AppInput
                  label="PIN"
                  isRequired
                  value={form.pin}
                  onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value }))}
                  placeholder="Auto-fills from India Post when possible"
                  helperText="Auto-filled for Kachchh localities when the village name matches a post office."
                />
                <AppInput
                  label="Name of Gram Panchayat"
                  value={form.gramPanchayat}
                  onChange={(e) => setForm((f) => ({ ...f, gramPanchayat: e.target.value }))}
                  helperText="May differ from village name in combined (Juth) panchayats."
                />
                <AppSelect
                  label="Category"
                  value={form.category}
                  onChange={(v) => setForm((f) => ({ ...f, category: v }))}
                  options={VILLAGE_CATEGORIES.map((c) => ({ value: c, label: c }))}
                />
              </SimpleGrid>

              <Box>
                <Flex justify="space-between" align="center" mb={2} gap={3} wrap="wrap">
                  <FormLabel mb={0}>Location on map</FormLabel>
                  <OutlineButton
                    size="sm"
                    type="button"
                    leftIcon={<MdMyLocation />}
                    isLoading={mapSearching}
                    onClick={handleLocateOnMap}
                  >
                    Search village on map
                  </OutlineButton>
                </Flex>
                {addModal.isOpen ? (
                  <VillageMap
                    key="add-village-map"
                    latitude={form.latitude}
                    longitude={form.longitude}
                    boundary={form.boundary}
                    clickable
                    height="260px"
                    hint="Type a Kachchh village name (or Search) to load the official border. Map does not use wheel-zoom so the dialog can scroll."
                    onPick={(lat, lng) => {
                      const official = lookupOfficialVillageGeo(
                        form.villageName,
                        form.talukaName,
                      );
                      if (official) {
                        setForm((f) => ({
                          ...f,
                          latitude: official.latitude,
                          longitude: official.longitude,
                          boundary: official.boundary,
                        }));
                        return;
                      }
                      setForm((f) => ({
                        ...f,
                        latitude: lat,
                        longitude: lng,
                        boundary: null,
                      }));
                    }}
                  />
                ) : null}
              </Box>

              <HStack justify="space-between">
                <FormLabel mb={0}>Active</FormLabel>
                <Switch
                  isChecked={form.active}
                  onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                  colorScheme="yellow"
                />
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter
            flexShrink={0}
            gap={2}
            borderTopWidth="1px"
            borderColor="border.subtle"
            bg="bg.surface"
            zIndex={2}
          >
            <OutlineButton type="button" onClick={addModal.onClose} isDisabled={saving}>
              Cancel
            </OutlineButton>
            <PrimaryButton type="submit" isLoading={saving}>
              Save
            </PrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View village details + map */}
      <Modal
        isOpen={detailModal.isOpen}
        onClose={detailModal.onClose}
        size="3xl"
        isCentered
        scrollBehavior="inside"
        blockScrollOnMount
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          bg="bg.surface"
          borderRadius="md"
          mx={4}
          my={4}
          maxH="90vh"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader flexShrink={0}>{selected?.villageName || 'Village'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody flex="1" overflowY="auto" minH={0} pb={4}>
            {selected ? (
              <VStack align="stretch" spacing={4}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <DetailRow label="Gram Panchayat" value={selected.gramPanchayat} />
                  <DetailRow label="Taluka" value={selected.talukaName} />
                  <DetailRow label="District" value={selected.district} />
                  <DetailRow label="State" value={selected.state} />
                  <DetailRow label="Country" value={selected.country} />
                  <DetailRow label="PIN" value={selected.pin} />
                  <DetailRow label="Category" value={selected.category} />
                </SimpleGrid>
                <ActiveBadge active={selected.active} />
                {detailModal.isOpen ? (
                  <VillageMap
                    key={`view-${selected.id ?? selected.villageId}`}
                    latitude={selected.latitude}
                    longitude={selected.longitude}
                    boundary={selected.boundary || null}
                    height="300px"
                    hint={
                      selected.boundary
                        ? 'Official village border (Gujarat village maps).'
                        : 'No official polygon on file — showing approximate area.'
                    }
                  />
                ) : null}
              </VStack>
            ) : null}
          </ModalBody>
          <ModalFooter
            flexShrink={0}
            borderTopWidth="1px"
            borderColor="border.subtle"
            bg="bg.surface"
            zIndex={2}
          >
            <PrimaryButton onClick={detailModal.onClose}>Close</PrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <Box
      p={3}
      bg="bg.input"
      borderRadius="sm"
      borderWidth="1px"
      borderColor="border.subtle"
    >
      <Text fontSize="11px" color="text.secondary" mb={0.5}>
        {label}
      </Text>
      <Text fontSize="14px" fontWeight="600" fontFamily={mono ? 'mono' : 'inherit'}>
        {value || '—'}
      </Text>
    </Box>
  );
}
